import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
  useMapEvents,
  LayerGroup,
  Circle,
  CircleMarker,
  Pane,
  ZoomControl,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SimulationActivity, type UnmatchedRide } from '../../../../types/simulation';
import {
  type GeoJsonLineString,
  type SegmentRouteMetadata,
} from '../../../../services/simulation/resultsPathsService';
import { unmatchedRideStableKey } from './utils';
import { MAP_UI_CORNER_INSET_PX } from '../../mapUiLayout';
import GoogleTileLayer from '../../GoogleTileLayer';
import { useGoogleBasemap } from '../../../../services/googleTiles/useGoogleBasemap';
import { getActivityCoordinates } from './DriverActivityDetailPanel';
import VehiclePlaybackLayer, { type VehiclePlaybackLayerProps } from './VehiclePlaybackLayer';
import { activityStopKindForUi } from '../../../../utils/simulation/inferActivityStopKind';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../../services/eventTaxonomy';
import type { SimulationResultsStopType } from '../../../../services/eventTypes';

function trackStopClick(objectType: SimulationResultsStopType): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_stop_clicked, {
    object_type: objectType,
    timestamp: new Date().toISOString(),
  });
}

// ─── Local type ──────────────────────────────────────────────────────────────

/** One unique road path between a departure→arrival address pair. */
type AddressPairRoute = {
  routeId: string;
  geometry: GeoJsonLineString;
  metadata?: SegmentRouteMetadata;
};

// ─── Geometry helpers ─────────────────────────────────────────────────────────

function haversineDistanceMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): number {
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const earthRadiusMeters = 6_371_000;
  const dLat = toRadians(bLat - aLat);
  const dLng = toRadians(bLng - aLng);
  const lat1 = toRadians(aLat);
  const lat2 = toRadians(bLat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function interpolateHexColor(startHex: string, endHex: string, t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const s = startHex.replace('#', '');
  const e = endHex.replace('#', '');
  const sr = parseInt(s.slice(0, 2), 16);
  const sg = parseInt(s.slice(2, 4), 16);
  const sb = parseInt(s.slice(4, 6), 16);
  const er = parseInt(e.slice(0, 2), 16);
  const eg = parseInt(e.slice(2, 4), 16);
  const eb = parseInt(e.slice(4, 6), 16);
  const r = Math.round(sr + (er - sr) * clamp);
  const g = Math.round(sg + (eg - sg) * clamp);
  const b = Math.round(sb + (eb - sb) * clamp);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

function densifyLine(from: [number, number], to: [number, number], steps = 16): [number, number][] {
  const points: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    points.push([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
  }
  return points;
}

// ─── Icon constants ───────────────────────────────────────────────────────────

const OP_GREEN = '#5FC98F';
const OP_ORANGE = '#EE7334';
const OP_GREY = '#7C8594';
const MAP_NODE_SIZE = 26;
const arrowUpSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
const arrowDownSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>';
const busSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="3" width="16" height="14" rx="2"/><path d="M4 10h16"/><path d="M8 17v3M16 17v3"/><circle cx="8" cy="20" r="1.2" fill="white"/><circle cx="16" cy="20" r="1.2" fill="white"/></svg>';

const MUTED_MARKER_GREY = '#B0BAC8';
const UNMATCHED_STOP_GREY = '#374151';

type ResultsMapIcons = {
  pickupIcon: L.DivIcon;
  dropoffIcon: L.DivIcon;
  pickupIconMuted: L.DivIcon;
  dropoffIconMuted: L.DivIcon;
  depotIcon: L.DivIcon;
  depotIconMuted: L.DivIcon;
  unmatchedPickupIcon: L.DivIcon;
  unmatchedDropoffIcon: L.DivIcon;
  unmatchedPickupIconMuted: L.DivIcon;
  unmatchedDropoffIconMuted: L.DivIcon;
};

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function buildResultsMapIcons(t: TFunction): ResultsMapIcons {
  const pickupTitle = escapeHtmlAttr(t('results.mapLayers.pickupTitle'));
  const dropoffTitle = escapeHtmlAttr(t('results.mapLayers.dropoffTitle'));
  const depotTitle = escapeHtmlAttr(t('results.mapLayers.depotTitle'));
  const unmatchedPickupTitle = escapeHtmlAttr(t('results.mapLayers.unmatchedPickupTitle'));
  const unmatchedDropoffTitle = escapeHtmlAttr(t('results.mapLayers.unmatchedDropoffTitle'));

  return {
    pickupIcon: L.divIcon({
      className: 'sim-pickup-marker',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
        background:${OP_GREEN};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.25);
        display:flex;align-items:center;justify-content:center;
      " title="${pickupTitle}">${arrowUpSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    dropoffIcon: L.divIcon({
      className: 'sim-dropoff-marker',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
        background:${OP_ORANGE};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.25);
        display:flex;align-items:center;justify-content:center;
      " title="${dropoffTitle}">${arrowDownSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    pickupIconMuted: L.divIcon({
      className: 'sim-pickup-marker sim-marker-muted',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.55;
        background:${MUTED_MARKER_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.15);
        display:flex;align-items:center;justify-content:center;
      " title="${pickupTitle}">${arrowUpSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    dropoffIconMuted: L.divIcon({
      className: 'sim-dropoff-marker sim-marker-muted',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.55;
        background:${MUTED_MARKER_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.15);
        display:flex;align-items:center;justify-content:center;
      " title="${dropoffTitle}">${arrowDownSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    depotIcon: L.divIcon({
      className: 'sim-depot-marker',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
        background:${OP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.25);
        display:flex;align-items:center;justify-content:center;
      " title="${depotTitle}">${busSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    depotIconMuted: L.divIcon({
      className: 'sim-depot-marker sim-marker-muted',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.36;
        background:${OP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);
        display:flex;align-items:center;justify-content:center;
      " title="${depotTitle}">${busSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    unmatchedPickupIcon: L.divIcon({
      className: 'sim-unmatched-pickup-marker',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
        background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
      " title="${unmatchedPickupTitle}">${arrowUpSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    unmatchedDropoffIcon: L.divIcon({
      className: 'sim-unmatched-dropoff-marker',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
        background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
      " title="${unmatchedDropoffTitle}">${arrowDownSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    unmatchedPickupIconMuted: L.divIcon({
      className: 'sim-unmatched-pickup-marker sim-marker-muted',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.32;
        background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);
        display:flex;align-items:center;justify-content:center;
      " title="${unmatchedPickupTitle}">${arrowUpSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
    unmatchedDropoffIconMuted: L.divIcon({
      className: 'sim-unmatched-dropoff-marker sim-marker-muted',
      html: `<div style="
        width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.32;
        background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);
        display:flex;align-items:center;justify-content:center;
      " title="${unmatchedDropoffTitle}">${arrowDownSvg}</div>`,
      iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
      iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
      popupAnchor: [0, -MAP_NODE_SIZE / 2],
    }),
  };
}

// ─── Inner map components (must render inside <MapContainer>) ─────────────────

/**
 * Fits the Leaflet map to the bounding box of the supplied points.
 * Only re-fits when `fitKey` changes (e.g. the selected run ID), so scrubbing
 * the timeline never triggers a new zoom animation.
 */
function FitBounds({
  points,
  fitKey,
}: {
  points: Record<string, unknown>[];
  fitKey: string;
}) {
  const map = useMap();
  const lastFitKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (fitKey === lastFitKey.current || points.length === 0) return;
    lastFitKey.current = fitKey;
    try {
      map.stop();
      const latlngs = points
        .filter((p) => typeof p.from_latitude === 'number' && typeof p.from_longitude === 'number')
        .map((p) => [p.from_latitude, p.from_longitude] as [number, number]);
      if (latlngs.length === 0) return;
      const bounds = L.latLngBounds(latlngs);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20], animate: false });
      }
    } catch {
      // map pane may have already been removed; nothing to do
    }
  }, [fitKey, points, map]);

  return null;
}

function ClearTripFocusOnMapClick({
  tripFocusActive,
  onClearTripFocus,
}: {
  tripFocusActive: boolean;
  onClearTripFocus: () => void;
}) {
  useMapEvents({
    click: (event) => {
      if (!tripFocusActive) return;
      const target = event.originalEvent?.target as HTMLElement | null;
      const clickedInteractive =
        target?.closest(
          '.leaflet-marker-icon, .leaflet-popup, .leaflet-control, .leaflet-interactive, .sim-map-point-popup'
        ) != null;
      if (!clickedInteractive) {
        onClearTripFocus();
      }
    },
  });
  return null;
}

function HighlightActivityMapEffect({
  highlightedActivity,
}: {
  highlightedActivity: { lat: number; lng: number; activityId?: string } | null;
}) {
  const map = useMap();
  React.useEffect(() => {
    if (!highlightedActivity) return;
    const zoom = map.getZoom();
    map.setView([highlightedActivity.lat, highlightedActivity.lng], Number.isFinite(zoom) ? zoom : 16);
  }, [highlightedActivity, map]);
  if (!highlightedActivity) return null;
  return (
    <Circle
      center={[highlightedActivity.lat, highlightedActivity.lng]}
      radius={80}
      pathOptions={{
        color: '#EE7334',
        fillColor: '#EE7334',
        fillOpacity: 0.25,
        weight: 3,
      }}
    />
  );
}

function UnmatchedRideMapFocusEffect({ ride }: { ride: UnmatchedRide | null }) {
  const map = useMap();
  React.useEffect(() => {
    if (!ride) return;
    const corners: [number, number][] = [];
    if (typeof ride.from_latitude === 'number' && typeof ride.from_longitude === 'number') {
      corners.push([ride.from_latitude, ride.from_longitude]);
    }
    if (typeof ride.to_latitude === 'number' && typeof ride.to_longitude === 'number') {
      corners.push([ride.to_latitude, ride.to_longitude]);
    }
    if (corners.length === 0) return;
    try {
      map.stop();
      const bounds = L.latLngBounds(corners);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [52, 52], animate: true, maxZoom: 16 });
      }
    } catch {
      // map pane may have already been removed
    }
  }, [ride, map]);
  return null;
}

/**
 * Halos at focused trip endpoints (pickup green, dropoff orange).
 * Uses the pre-built `matchingActivitySet` for O(1) activity membership checks
 * instead of re-deriving match keys on every render.
 */
function TripStopsHighlightCircles({
  matchingActivitySet,
  activities,
  fallbackRidePoint,
  focusKey,
}: {
  matchingActivitySet: Set<SimulationActivity>;
  activities: SimulationActivity[] | undefined;
  fallbackRidePoint: Record<string, unknown> | null;
  focusKey: string;
}) {
  if (matchingActivitySet.size === 0) return null;
  const positions: Array<{ center: [number, number]; kind: 'pickup' | 'dropoff' }> = [];
  if (activities?.length) {
    activities.forEach((activity) => {
      if (!matchingActivitySet.has(activity)) return;
      const coords = getActivityCoordinates(activity);
      if (!coords) return;
      const kind = activityStopKindForUi(activity);
      if (kind === 'pickup' || kind === 'dropoff') {
        positions.push({ center: [coords.lat, coords.lng], kind });
      }
    });
  }
  if (positions.length === 0 && fallbackRidePoint) {
    const fromLat = fallbackRidePoint.from_latitude;
    const fromLng = fallbackRidePoint.from_longitude;
    const toLat = fallbackRidePoint.to_latitude;
    const toLng = fallbackRidePoint.to_longitude;
    if (typeof fromLat === 'number' && typeof fromLng === 'number') {
      positions.push({ center: [fromLat, fromLng], kind: 'pickup' });
    }
    if (typeof toLat === 'number' && typeof toLng === 'number') {
      positions.push({ center: [toLat, toLng], kind: 'dropoff' });
    }
  }
  if (positions.length === 0) return null;
  return (
    <Pane name="tripHighlightStops" style={{ zIndex: 560 }}>
      {positions.map(({ center, kind }, idx) => (
        <Circle
          key={`trip-stop-${kind}-${focusKey}-${idx}-${center[0]}-${center[1]}`}
          center={center}
          radius={88}
          pathOptions={{
            color: kind === 'pickup' ? '#5FC98F' : '#EE7334',
            fillColor: kind === 'pickup' ? '#5FC98F' : '#EE7334',
            fillOpacity: 0.22,
            weight: 3,
            opacity: 1,
          }}
        />
      ))}
    </Pane>
  );
}

/** Halos at focused unallocated trip endpoints (neutral grey, distinct from allocated green/orange). */
function UnmatchedTripStopsHighlightCircles({ ride }: { ride: UnmatchedRide | null }) {
  if (!ride) return null;
  const fromLat = ride.from_latitude;
  const fromLng = ride.from_longitude;
  const toLat = ride.to_latitude;
  const toLng = ride.to_longitude;
  const positions: [number, number][] = [];
  if (typeof fromLat === 'number' && typeof fromLng === 'number') {
    positions.push([fromLat, fromLng]);
  }
  if (typeof toLat === 'number' && typeof toLng === 'number') {
    positions.push([toLat, toLng]);
  }
  if (positions.length === 0) return null;
  const keyTag = unmatchedRideStableKey(ride);
  return (
    <Pane name="unmatchedHighlightStops" style={{ zIndex: 562 }}>
      {positions.map((center, idx) => (
        <Circle
          key={`unmatched-stop-${keyTag}-${idx}-${center[0]}-${center[1]}`}
          center={center}
          radius={88}
          pathOptions={{
            color: UNMATCHED_STOP_GREY,
            fillColor: UNMATCHED_STOP_GREY,
            fillOpacity: 0.2,
            weight: 3,
            opacity: 1,
          }}
        />
      ))}
    </Pane>
  );
}

// Pane drawn on its own canvas. Keep it below markerPane (600), and only the topmost canvas may be interactive:
// Leaflet sends a canvas click to the top canvas only.
function CanvasPane({
  name,
  zIndex,
  children,
}: {
  name: string;
  zIndex: number;
  children: (renderer: L.Canvas) => React.ReactNode;
}) {
  const [renderer] = useState(() => L.canvas({ pane: name, padding: 0.5 }));
  useEffect(() => () => void renderer.remove(), [renderer]);
  return (
    <Pane name={name} style={{ zIndex }}>
      {children(renderer)}
    </Pane>
  );
}

// ponytail: fixed cutoff for canvas dots; cluster with leaflet.markercluster if dense runs get unreadable.
const ICON_MARKER_LIMIT = 500;
const STOP_DOT_COLOR = { pickup: OP_GREEN, dropoff: OP_ORANGE, depot: OP_GREY } as const;

function stopDotOptions(kind: keyof typeof STOP_DOT_COLOR, dim: boolean) {
  return { color: '#fff', weight: 1, fillColor: dim ? MUTED_MARKER_GREY : STOP_DOT_COLOR[kind], fillOpacity: 1 };
}

// ─── Props interface ──────────────────────────────────────────────────────────

export interface ResultsMapLayersProps {
  /** Center coordinates for the map viewport. */
  mapCenter: [number, number];
  mapZoom: number;
  /** Full ridePoints array — used for FitBounds only. */
  points: Record<string, unknown>[];
  /**
   * Stable key that triggers FitBounds. Must be derived from the loaded data
   * (e.g. selectedSim?.run_id), NOT selectedRunId — otherwise FitBounds fires
   * with stale points before the new run's ridePoints have arrived.
   */
  fitKey: string;

  /** Ride points currently visible after time-window and vehicle-visibility filters. */
  visiblePointsToShow: Record<string, unknown>[];
  /** Memoized activity markers to render. */
  activitiesToShow: {
    position: [number, number];
    activity: SimulationActivity;
    markerType: 'pickup' | 'dropoff' | 'depot';
  }[];
  /** Unallocated rides with valid map coordinates. */
  unmatchedRidesMap: UnmatchedRide[];
  /** Road routes visible for the selected time window. */
  visibleRoadRoutes: AddressPairRoute[];
  /** Routes that match the focused trip — memoized via OPT-9. */
  focusedRoadRoutes: AddressPairRoute[];
  /** Merged gradient path for the focused road route (pre-computed in parent). */
  focusedRoadGradientPathSafe: [number, number][];
  focusedRoadGradientSegmentCount: number;
  showRoadPolylines: boolean;
  /** Vehicle trails and positions at the timeline time; drawn only with road polylines. */
  playback?: VehiclePlaybackLayerProps | null;

  /** Whether any trip is currently focused via match-key highlight. */
  tripFocusActive: boolean;
  /** True when any focus-like mode dims non-selected items (trip or unmatched ride). */
  mapTripFocusActive: boolean;
  highlightedTripMatchKeys: string[];
  /** OPT-9: pre-built Set for O(1) match-key lookups. */
  highlightedTripMatchKeySet: Set<string>;
  highlightedTripRidePoint: Record<string, unknown> | null;
  /** Shift IDs belonging to the focused trip — memoized Set. */
  highlightedTripShiftIds: Set<string>;
  highlightedTripEndpoints: { pickup?: [number, number]; dropoff?: [number, number] };
  /** Pre-built set of activities that belong to the focused trip (OPT-9). */
  matchingActivitySet: Set<SimulationActivity>;
  /** Pre-built set of ride-point objects that belong to the focused trip (OPT-9). */
  matchingRidePointSet: Set<unknown>;
  /** All activities for the selected simulation run. */
  activities: SimulationActivity[];

  highlightedActivity: { lat: number; lng: number; activityId?: string } | null;
  highlightedUnmatchedRide: UnmatchedRide | null;

  /** Stable callback — called when map background is clicked while trip is focused. */
  onClearTripFocus: () => void;
  /** Stable callback — called when an activity marker is clicked. */
  onSelectActivityMarker: (
    activity: SimulationActivity,
    markerType: 'pickup' | 'dropoff' | 'other'
  ) => void;
  /** Stable callback — called when a ride-point marker is clicked. */
  onSelectRidePoint: (p: Record<string, unknown>, markerType: 'pickup' | 'dropoff') => void;
  /** Stable callback — called when an unmatched-ride marker is clicked. */
  onSelectUnmatchedRide: (ride: UnmatchedRide) => void;

  ridesError: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

function ResultsMapLayers({
  mapCenter,
  mapZoom,
  points,
  fitKey,
  visiblePointsToShow,
  activitiesToShow,
  unmatchedRidesMap,
  visibleRoadRoutes,
  focusedRoadRoutes,
  focusedRoadGradientPathSafe,
  focusedRoadGradientSegmentCount,
  showRoadPolylines,
  playback,
  tripFocusActive,
  mapTripFocusActive,
  highlightedTripMatchKeys,
  highlightedTripRidePoint,
  highlightedTripEndpoints,
  matchingActivitySet,
  matchingRidePointSet,
  activities,
  highlightedActivity,
  highlightedUnmatchedRide,
  onClearTripFocus,
  onSelectActivityMarker,
  onSelectRidePoint,
  onSelectUnmatchedRide,
  ridesError,
}: ResultsMapLayersProps) {
  const { t, i18n } = useTranslation('simulation');
  const googleBasemap = useGoogleBasemap();
  // Rebuild divIcons whenever the active language changes so embedded title="" tooltips re-translate.
  const {
    pickupIcon,
    dropoffIcon,
    pickupIconMuted,
    dropoffIconMuted,
    depotIcon,
    depotIconMuted,
    unmatchedPickupIcon,
    unmatchedDropoffIcon,
    unmatchedPickupIconMuted,
    unmatchedDropoffIconMuted,
  } = useMemo(() => buildResultsMapIcons(t), [t, i18n.language]);
  if (ridesError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 2,
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography color="error">{ridesError}</Typography>
      </Box>
    );
  }

  if (points.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography sx={{ color: '#3A3A48', opacity: 0.7 }}>
          {t('results.mapLayers.selectRunPrompt')}
        </Typography>
      </Box>
    );
  }

  // O(1) set for the base-pane dimming check — avoids re-running vehicleRouteMatchesTrip per route.
  const focusedRouteIdSet = new Set(focusedRoadRoutes.map((r) => r.routeId));
  const stopCount = activitiesToShow.length > 0 ? activitiesToShow.length : visiblePointsToShow.length * 2;

  const stopIcons = {
    pickup: [pickupIcon, pickupIconMuted],
    dropoff: [dropoffIcon, dropoffIconMuted],
    depot: [depotIcon, depotIconMuted],
  } as const;

  // One stop: a canvas dot when `dotRenderer` is set (large runs), the icon marker otherwise.
  const stopElement = (
    key: string,
    position: [number, number],
    kind: 'pickup' | 'dropoff' | 'depot',
    dim: boolean,
    focused: boolean,
    onClick: () => void,
    dotRenderer?: L.Canvas
  ) =>
    dotRenderer ? (
      // Focused remounts so it draws and hit-tests last; no bubbling, or the map click clears trip focus.
      <CircleMarker
        key={focused ? `${key}-focused` : key}
        renderer={dotRenderer}
        bubblingMouseEvents={false}
        center={position}
        radius={focused ? 7 : 5}
        pathOptions={stopDotOptions(kind, dim)}
        eventHandlers={{ click: onClick }}
      />
    ) : (
      <Marker
        key={key}
        position={position}
        icon={stopIcons[kind][dim ? 1 : 0]}
        zIndexOffset={focused ? 2200 : 0}
        eventHandlers={{ click: onClick }}
      />
    );

  const renderStops = (dotRenderer?: L.Canvas) =>
    activitiesToShow.length > 0
      ? activitiesToShow.map(({ position, activity, markerType }, idx) => {
          const endpointForType =
            markerType === 'pickup'
              ? highlightedTripEndpoints.pickup
              : markerType === 'dropoff'
                ? highlightedTripEndpoints.dropoff
                : undefined;
          const isSelectedTripEndpointMarker =
            tripFocusActive &&
            !!endpointForType &&
            haversineDistanceMeters(
              position[0],
              position[1],
              endpointForType[0],
              endpointForType[1]
            ) <= 60;
          const isTripFocus =
            tripFocusActive &&
            (matchingActivitySet.has(activity) || isSelectedTripEndpointMarker);
          const dim = mapTripFocusActive && !isTripFocus;
          const role = markerType === 'depot' ? 'other' : markerType;
          return stopElement(
            `activity-${markerType}-${activity.ride_id ?? activity.vehicle_id ?? 'na'}-${activity.activity_start_time ?? activity.activity_start ?? 'na'}-${idx}`,
            position,
            markerType,
            dim,
            isTripFocus,
            () => {
              trackStopClick(role === 'other' ? 'hub' : 'stop');
              onSelectActivityMarker(activity, role);
            },
            dotRenderer
          );
        })
      : visiblePointsToShow.map((p, idx) => {
          const from: [number, number] | undefined =
            typeof p.from_latitude === 'number' && typeof p.from_longitude === 'number'
              ? [p.from_latitude, p.from_longitude]
              : undefined;
          const to: [number, number] | undefined =
            typeof p.to_latitude === 'number' && typeof p.to_longitude === 'number'
              ? [p.to_latitude, p.to_longitude]
              : undefined;
          const isSelectedTripMarker = matchingRidePointSet.has(p);
          const dim = mapTripFocusActive && !isSelectedTripMarker;
          const rideId =
            typeof p.ride_id === 'string'
              ? p.ride_id
              : typeof p.ride_id === 'number'
                ? String(p.ride_id)
                : `ride-${idx}`;
          const select = (leg: 'pickup' | 'dropoff') => () => {
            trackStopClick('stop');
            onSelectRidePoint(p, leg);
          };
          return (
            <React.Fragment key={`${rideId}-markers`}>
              {from && stopElement('pickup', from, 'pickup', dim, isSelectedTripMarker, select('pickup'), dotRenderer)}
              {to && stopElement('dropoff', to, 'dropoff', dim, isSelectedTripMarker, select('dropoff'), dotRenderer)}
            </React.Fragment>
          );
        });

  return (
    <MapContainer
      className="atlas-simulation-results-map"
      style={
        {
          width: '100%',
          height: '100%',
          ['--map-ui-inset' as string]: `${MAP_UI_CORNER_INSET_PX}px`,
        } as React.CSSProperties
      }
      center={mapCenter}
      zoom={mapZoom}
      scrollWheelZoom={true}
      dragging={true}
      doubleClickZoom={true}
      zoomControl={false}
      attributionControl={true}
      keyboard={true}
      touchZoom={true}
      boxZoom={true}
    >
      <ZoomControl position="bottomright" />
      {/* ponytail: always roadmap, ignores the user's basemap choice. */}
      {googleBasemap.apiKey ? (
        <GoogleTileLayer apiKey={googleBasemap.apiKey} mapType="roadmap" />
      ) : (
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
      )}
      <FitBounds points={points} fitKey={fitKey} />
      <ClearTripFocusOnMapClick
        tripFocusActive={mapTripFocusActive}
        onClearTripFocus={onClearTripFocus}
      />
      {!tripFocusActive && !highlightedUnmatchedRide && (
        <HighlightActivityMapEffect highlightedActivity={highlightedActivity} />
      )}
      {highlightedUnmatchedRide != null && !tripFocusActive && (
        <UnmatchedRideMapFocusEffect ride={highlightedUnmatchedRide} />
      )}
      {tripFocusActive && (
        <TripStopsHighlightCircles
          matchingActivitySet={matchingActivitySet}
          activities={activities}
          fallbackRidePoint={highlightedTripRidePoint}
          focusKey={highlightedTripMatchKeys.slice(0, 3).join('|')}
        />
      )}
      {highlightedUnmatchedRide != null && !tripFocusActive && (
        <UnmatchedTripStopsHighlightCircles ride={highlightedUnmatchedRide} />
      )}

      {/* Unmatched / unallocated OD lines */}
      {unmatchedRidesMap.length > 0 && (
        <>
          <CanvasPane name="unmatchedODBase" zIndex={435}>
            {(renderer) => unmatchedRidesMap.map((ride) => {
              const from: [number, number] = [ride.from_latitude!, ride.from_longitude!];
              const to: [number, number] = [ride.to_latitude!, ride.to_longitude!];
              const rideKey = unmatchedRideStableKey(ride);
              const isSel =
                highlightedUnmatchedRide != null &&
                unmatchedRideStableKey(highlightedUnmatchedRide) === rideKey;
              const dim = mapTripFocusActive && !isSel;
              if (isSel) return null;
              return (
                <Polyline
                  key={`unmatched-od-${rideKey}`}
                  renderer={renderer}
                  interactive={false}
                  positions={[from, to]}
                  pathOptions={{
                    color: '#6b7280',
                    weight: 2,
                    opacity: dim ? 0.12 : 0.7,
                    dashArray: '6 10',
                  }}
                />
              );
            })}
          </CanvasPane>
          <Pane name="unmatchedODSelected" style={{ zIndex: 685 }}>
            {unmatchedRidesMap.map((ride) => {
              const from: [number, number] = [ride.from_latitude!, ride.from_longitude!];
              const to: [number, number] = [ride.to_latitude!, ride.to_longitude!];
              const rideKey = unmatchedRideStableKey(ride);
              const isSel =
                highlightedUnmatchedRide != null &&
                unmatchedRideStableKey(highlightedUnmatchedRide) === rideKey;
              if (!isSel) return null;
              const denseLine = densifyLine(from, to, 16);
              const segmentCount = Math.max(1, denseLine.length - 1);
              return (
                <React.Fragment key={`unmatched-sel-${rideKey}`}>
                  {denseLine.slice(0, -1).map((start, index) => {
                    const end = denseLine[index + 1]!;
                    const t = segmentCount <= 1 ? 1 : index / (segmentCount - 1);
                    return (
                      <React.Fragment key={`unmatched-sel-${rideKey}-${index}`}>
                        <Polyline
                          positions={[start, end]}
                          pathOptions={{
                            color: '#9ca3af',
                            weight: 9,
                            opacity: 0.55,
                          }}
                        />
                        <Polyline
                          positions={[start, end]}
                          pathOptions={{
                            color: interpolateHexColor('#D1D5DB', '#1F2937', t),
                            weight: 5,
                            opacity: 1,
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </Pane>
        </>
      )}

      {/* Road paths from API, or straight O–D lines until /paths returns routes. */}
      {showRoadPolylines ? (
        <>
          <CanvasPane name="roadPathsBase" zIndex={440}>
            {(renderer) => visibleRoadRoutes.map(({ routeId, geometry }) => {
              const positions = geometry.coordinates.map(
                ([lng, lat]) => [lat, lng] as [number, number]
              );
              const isHit = focusedRouteIdSet.has(routeId);
              const dim = mapTripFocusActive && !isHit;
              if (isHit) return null;
              return (
                <Polyline
                  key={`road-route-${routeId}`}
                  renderer={renderer}
                  interactive={false}
                  positions={positions}
                  pathOptions={{
                    color: '#1976d2',
                    weight: 3,
                    opacity: dim ? 0.06 : 0.75,
                  }}
                />
              );
            })}
          </CanvasPane>
          <Pane name="roadPathsSelected" style={{ zIndex: 680 }}>
            {focusedRoadGradientPathSafe.slice(0, -1).map((start, index) => {
              const end = focusedRoadGradientPathSafe[index + 1]!;
              const t =
                focusedRoadGradientSegmentCount <= 1
                  ? 1
                  : index / (focusedRoadGradientSegmentCount - 1);
              return (
                <React.Fragment key={`road-route-hit-merged-${index}`}>
                  <Polyline
                    positions={[start, end]}
                    pathOptions={{
                      color: '#9ca3af',
                      weight: 10,
                      opacity: 0.58,
                    }}
                  />
                  <Polyline
                    positions={[start, end]}
                    pathOptions={{
                      color: interpolateHexColor('#8BFFD1', '#FF9B5F', t),
                      weight: 7,
                      opacity: 1,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </Pane>
          {playback && <VehiclePlaybackLayer {...playback} />}
        </>
      ) : (
        <>
          <CanvasPane name="straightODBase" zIndex={440}>
            {(renderer) => visiblePointsToShow.map((p, idx) => {
              const from: [number, number] | undefined =
                typeof p.from_latitude === 'number' && typeof p.from_longitude === 'number'
                  ? [p.from_latitude, p.from_longitude]
                  : undefined;
              const to: [number, number] | undefined =
                typeof p.to_latitude === 'number' && typeof p.to_longitude === 'number'
                  ? [p.to_latitude, p.to_longitude]
                  : undefined;
              const rideId =
                typeof p.ride_id === 'string'
                  ? p.ride_id
                  : typeof p.ride_id === 'number'
                    ? String(p.ride_id)
                    : `ride-${idx}`;
              return (
                <React.Fragment key={rideId}>
                  {from && to && (
                    <Polyline
                      renderer={renderer}
                      interactive={false}
                      positions={[from, to]}
                      pathOptions={{
                        color: '#1976d2',
                        weight: 2,
                        opacity: mapTripFocusActive ? 0.18 : 0.7,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </CanvasPane>
          <Pane name="straightODSelected" style={{ zIndex: 680 }}>
            {tripFocusActive &&
              (() => {
                // Mirror TripStopsHighlightCircles: prefer activity coords, fall back to ride point
                let odFrom: [number, number] | undefined;
                let odTo: [number, number] | undefined;
                if (activities?.length) {
                  for (const activity of activities) {
                    if (!matchingActivitySet.has(activity)) continue;
                    const coords = getActivityCoordinates(activity);
                    if (!coords) continue;
                    const kind = activityStopKindForUi(activity);
                    if (kind === 'pickup' && !odFrom) odFrom = [coords.lat, coords.lng];
                    if (kind === 'dropoff' && !odTo) odTo = [coords.lat, coords.lng];
                  }
                }
                if (!odFrom) odFrom = highlightedTripEndpoints.pickup;
                if (!odTo) odTo = highlightedTripEndpoints.dropoff;
                if (!odFrom || !odTo) return null;
                const denseLine = densifyLine(odFrom, odTo, 16);
                const segmentCount = Math.max(1, denseLine.length - 1);
                return denseLine.slice(0, -1).map((start, index) => {
                  const end = denseLine[index + 1]!;
                  const t = segmentCount <= 1 ? 1 : index / (segmentCount - 1);
                  return (
                    <React.Fragment key={`od-selected-${index}`}>
                      <Polyline
                        positions={[start, end]}
                        pathOptions={{
                          color: '#9ca3af',
                          weight: 9,
                          opacity: 0.58,
                        }}
                      />
                      <Polyline
                        positions={[start, end]}
                        pathOptions={{
                          color: interpolateHexColor('#8BFFD1', '#FF9B5F', t),
                          weight: 6,
                          opacity: 1,
                        }}
                      />
                    </React.Fragment>
                  );
                });
              })()}
          </Pane>
        </>
      )}

      {/* Activity / ride-point stops */}
      {stopCount > ICON_MARKER_LIMIT ? (
        <CanvasPane name="stopDots" zIndex={590}>
          {(renderer) => renderStops(renderer)}
        </CanvasPane>
      ) : (
        <LayerGroup>{renderStops()}</LayerGroup>
      )}

      {/* Unmatched-ride markers stay icons: unmatched rides are few, and their icons sit above the stop dots. */}
      <LayerGroup>
        {unmatchedRidesMap.map((ride) => {
          const rideKey = unmatchedRideStableKey(ride);
          const isSel =
            highlightedUnmatchedRide != null &&
            unmatchedRideStableKey(highlightedUnmatchedRide) === rideKey;
          const dim = mapTripFocusActive && !isSel;
          const from: [number, number] = [ride.from_latitude!, ride.from_longitude!];
          const to: [number, number] = [ride.to_latitude!, ride.to_longitude!];
          return (
            <React.Fragment key={`unmatched-m-${rideKey}`}>
              <Marker
                position={from}
                icon={dim ? unmatchedPickupIconMuted : unmatchedPickupIcon}
                zIndexOffset={isSel ? 2400 : 150}
                eventHandlers={{ click: () => onSelectUnmatchedRide(ride) }}
              />
              <Marker
                position={to}
                icon={dim ? unmatchedDropoffIconMuted : unmatchedDropoffIcon}
                zIndexOffset={isSel ? 2400 : 150}
                eventHandlers={{ click: () => onSelectUnmatchedRide(ride) }}
              />
            </React.Fragment>
          );
        })}
      </LayerGroup>
    </MapContainer>
  );
}

export default React.memo(ResultsMapLayers);
